"use client";

import { createContext, useContext, useState, ReactNode } from "react";

export type ThemeType = "dark" | "midnight" | "forest" | "rose";

interface Settings {
  fontSize: "small" | "medium" | "large";
  showToolCalls: boolean;
  compactMode: boolean;
  theme: ThemeType;
}

interface SettingsContextType {
  settings: Settings;
  updateSettings: (newSettings: Partial<Settings>) => void;
}

const defaultSettings: Settings = {
  fontSize: "medium",
  showToolCalls: true,
  compactMode: false,
  theme: "dark",
};

// 主题配置
export const themes = {
  dark: {
    name: "暗夜",
    bg: "#1a1a1a",
    bgSecondary: "#2a2a2a",
    bgTertiary: "#3a3a3a",
    text: "#e5e5e5",
    textSecondary: "#a3a3a3",
    textMuted: "#808080",
    accent: "#b45309",
    border: "#3a3a3a",
  },
  midnight: {
    name: "深邃",
    bg: "#0f172a",
    bgSecondary: "#1e293b",
    bgTertiary: "#334155",
    text: "#e2e8f0",
    textSecondary: "#94a3b8",
    textMuted: "#64748b",
    accent: "#3b82f6",
    border: "#334155",
  },
  forest: {
    name: "森林",
    bg: "#0f1f15",
    bgSecondary: "#1a2f22",
    bgTertiary: "#2d4a3a",
    text: "#d1e7d4",
    textSecondary: "#9ac9a0",
    textMuted: "#6b9970",
    accent: "#22c55e",
    border: "#2d4a3a",
  },
  rose: {
    name: "玫瑰",
    bg: "#1f0f18",
    bgSecondary: "#2f1a25",
    bgTertiary: "#4a2d3a",
    text: "#f5e6ec",
    textSecondary: "#d4a5b8",
    textMuted: "#a06b7f",
    accent: "#ec4899",
    border: "#4a2d3a",
  },
};

const SettingsContext = createContext<SettingsContextType | undefined>(undefined);

export function SettingsProvider({ children }: { children: ReactNode }) {
  const [settings, setSettings] = useState<Settings>(defaultSettings);

  const updateSettings = (newSettings: Partial<Settings>) => {
    setSettings((prev) => ({ ...prev, ...newSettings }));
  };

  return (
    <SettingsContext.Provider value={{ settings, updateSettings }}>
      {children}
    </SettingsContext.Provider>
  );
}

export function useSettings() {
  const context = useContext(SettingsContext);
  if (!context) {
    throw new Error("useSettings must be used within a SettingsProvider");
  }
  return context;
}
